import { useEffect, useRef, useState } from "react";
import type { Telemetry } from "../hooks/useFpv";
import type { useFlightTape } from "../hooks/useFlightTape";

/**
 * FPV on-screen display: crosshair, armed state, throttle/speed/altitude, and a
 * controls hint. Reads the telemetry ref on a light interval so the 60 fps
 * flight loop never triggers a React render. The proximity streaks and crash
 * flash update at full frame rate via direct style writes (rAF on refs) — the
 * project's off-React rule for per-frame values.
 */
export function FpvHud({
  telemetry,
  connected,
  tape,
}: {
  telemetry: React.MutableRefObject<Telemetry>;
  connected: boolean;
  tape: ReturnType<typeof useFlightTape>;
}) {
  const [t, setT] = useState<Telemetry>({ throttle: 0, speed: 0, alt: 0, armed: false, crash: 0, prox: [0, 0, 0, 0] });
  const edgeL = useRef<HTMLDivElement>(null);
  const edgeR = useRef<HTMLDivElement>(null);
  const edgeT = useRef<HTMLDivElement>(null);
  const edgeB = useRef<HTMLDivElement>(null);
  const flash = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = window.setInterval(() => setT({ ...telemetry.current }), 90);
    return () => window.clearInterval(id);
  }, [telemetry]);

  useEffect(() => {
    let raf = 0;
    const set = (el: HTMLDivElement | null, v: number) => { if (el) el.style.opacity = String(Math.min(1, v)); };
    const tick = () => {
      const tel = telemetry.current;
      set(edgeL.current, tel.prox[0]);
      set(edgeR.current, tel.prox[1]);
      set(edgeT.current, tel.prox[2]);
      set(edgeB.current, tel.prox[3]);
      if (flash.current) {
        const age = performance.now() - tel.crash;
        flash.current.style.opacity = tel.crash && age < 700 ? String(0.85 * (1 - age / 700)) : "0";
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [telemetry]);

  return (
    <div className="fpv-hud">
      <div ref={edgeL} className="fpv-streak l" />
      <div ref={edgeR} className="fpv-streak r" />
      <div ref={edgeT} className="fpv-streak t" />
      <div ref={edgeB} className="fpv-streak b" />
      <div ref={flash} className="fpv-flash">CRASH</div>
      <div className="fpv-cross" />
      <div className="fpv-osd tl">
        <span className={"fpv-armed" + (t.armed ? " on" : "")}>{t.armed ? "● ARMED" : "○ DISARMED"}</span>
      </div>
      {tape.recording && (
        <div className="fpv-osd tr">
          {/* the 90 ms telemetry interval re-renders us, so the ref-read stays fresh */}
          <span className="fpv-rec">● REC {Math.floor(tape.recSeconds() / 60)}:{String(Math.floor(tape.recSeconds() % 60)).padStart(2, "0")} / 1:00</span>
        </div>
      )}
      <div className="fpv-osd bl">
        <div>THR {Math.round(t.throttle * 100)}%</div>
        <div>SPD {t.speed.toFixed(1)}</div>
        <div>ALT {t.alt.toFixed(1)}</div>
      </div>
      <div className="fpv-hint">
        {connected
          ? "gamepad · left stick: throttle + yaw · right stick: pitch + roll · A: arm · R: rec"
          : "keyboard · W/S: throttle · A/D: yaw · ↑↓: pitch · ←→: roll · Space: arm · R: rec"}
      </div>
    </div>
  );
}
